import React, { useEffect, useRef, useState } from 'react';
import Modal from '@material-ui/core/Modal';
import PropTypes from 'prop-types';
import style from './ExportModal.module.css';
import CloseWindowButton from '../../../../atomic/buttons/closeWindow/CloseWindowButton';
import { first } from 'rxjs/operators';
import DataList from '../../../../atomic/inputs/datalist/DataList';
import TagInput from '../../../../atomic/inputs/tagInput/TagInput';
import TextButton from '../../../../atomic/buttons/textButton/TextButton';
import { IconButton } from '@material-ui/core';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import { colors } from '../../../../../colorVars';
import { retrieveUserTemplates } from '../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/importexporttradeplan/retrieveUserTemplates';
import { exportTradePlan } from '../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/importexporttradeplan/exportTradePlan';
import { useWebsocketStore } from '../../../../../stores/websocketStore/websocketProvider';
import { observer } from 'mobx-react-lite';

const mockUserData = {
  messageType: 'existingExports',
  templates: [],
};
const ExportModal = observer(({ open, handleClose, data }) => {
  const { exportTemplateToHomeSubject, exportUserAndTemplateNamesSubject } =
    useWebsocketStore();

  const [templateTitle, setTemplateTitle] = useState('');
  const [incomingMessage, setIncomingMessage] = useState('');
  const [existing, setExisting] = useState(false);
  const [currentHash, setCurrentHash] = useState('');
  const [userData, setUserData] = useState(mockUserData);
  const [tagHistory, setTagHistory] = useState([]);
  const [tags, setTags] = useState([]);
  const [description, setDescription] = useState('');
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef(null);

  const resetState = () => {
    setTemplateTitle('');
    setIncomingMessage('');
    setExisting(false);
    setTags([]);
    setDescription('');
    setCurrentHash('');
  };
  const retrieveTags = (incomingTags) => {
    setTags(incomingTags);
  };
  const copyToTwitter = () => {
    const tagString = existing.tags.reduce(
      (acc, item) => acc + ' %23' + item.split(' ').join(''),
      '%23TradeUnafraid'
    );
    let text =
      'Use the code below to try my new @TUnafraid trade plan!%0D%0ATitle: ' +
      templateTitle +
      '%0D%0ADescription: ' +
      existing.description +
      '%0D%0ACode: ' +
      currentHash +
      '%0D%0A' +
      tagString;
    if (text.length > 300) {
      const diff = text.length - 300;
      const descriptionLength = existing.description.length;
      const sliceIndex = descriptionLength - (diff + 10);
      const newDescription = existing.description.slice(0, sliceIndex) + '...';
      text =
        'Use the code below to try my new @TUnafraid trade plan!%0D%0ATitle: ' +
        templateTitle +
        '%0D%0ADescription: ' +
        newDescription +
        '%0D%0ACode: ' +
        currentHash +
        '%0D%0A' +
        tagString;
    }
    const win = window.open(
      `https://twitter.com/intent/tweet?text=${text}`,
      '_blank'
    );
    win.focus();
  };
  const copyToClipboard = () => {
    const tagString = existing.tags.reduce(
      (acc, item) => acc + ' #' + item.split(' ').join(''),
      '#TradeUnafraid'
    );
    const text =
      'Title: ' +
      templateTitle +
      '\nDescription: ' +
      existing.description +
      '\nCode: ' +
      currentHash +
      '\n' +
      tagString;
    navigator.clipboard.writeText(text);
    setIncomingMessage({
      type: 'success',
      reason: 'Message copied!',
    });
  };

  useEffect(() => {
    const subscription = exportUserAndTemplateNamesSubject.subscribe((msg) => {
      setUserData(msg);
      setTagHistory(
        Array.from(
          new Set(
            msg.templates.reduce((acc, item) => {
              return [...acc, ...item.tags];
            }, [])
          )
        )
      );
    });
    return () => {
      subscription.unsubscribe();
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  useEffect(() => {
    setIncomingMessage('');
    if (
      userData?.templates.some((item) => item.templateName === templateTitle)
    ) {
      const selected = userData.templates.find(
        (template) => template.templateName === templateTitle
      );
      setExisting(selected);
      setCurrentHash(selected.hash);
    } else {
      setExisting(false);
      setCurrentHash('');
    }
  }, [templateTitle]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      onBackdropClick={() => {
        handleClose();
        resetState();
      }}
    >
      <div className={style.exportModalContainer}>
        <CloseWindowButton
          width={20}
          stroke={2}
          color="black"
          handleClick={() => {
            handleClose();
            resetState();
          }}
        />
        <div className={style.innerContainer}>
          <h5>Share Template</h5>
          <div className={style.copy}>
            <p>
              Select a previously exported template or export a new one to
              share. Other users may use your template code to import your
              template into their trade plan.
            </p>
          </div>
          <div className={style.inputs}>
            <div className={style.textInputs}>
              <DataList
                dataList={userData.templates}
                dataMapFunc={(data) => {
                  return (
                    <option value={data.templateName} key={data.hash}>
                      {new Date(data.timestamp).toDateString()}
                    </option>
                  );
                }}
                inputVal={templateTitle}
                inputHandler={(value) => setTemplateTitle(value)}
                id="templateTitle"
                width="500px"
              >
                Template title...
              </DataList>

              {!existing ? (
                <textarea
                  cols={39}
                  className={style.textArea}
                  placeholder="Description..."
                  value={description}
                  onChange={({ target }) => setDescription(target.value)}
                />
              ) : (
                <p className={style.existingDescription}>
                  <span className={style.bold}>Description: </span>{' '}
                  {existing?.description}
                </p>
              )}
            </div>

            {!existing ? (
              <TagInput
                prevTags={tagHistory}
                retrieveCurrentTags={retrieveTags}
              />
            ) : (
              <div className={style.existingTagDiv}>
                {existing?.tags.map((tag) => (
                  <span className={style.existingTags} key={tag}>
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>

          {}
          {!existing ? (
            <div>
              <p
                className={
                  incomingMessage.type === 'error' ? style.error : style.success
                }
              >
                {incomingMessage.reason}
              </p>
              <TextButton
                fontSize="medium"
                backgroundColor={colors.blue}
                color="white"
                width="100%"
                clickHandler={() => {
                  if (templateTitle === '') {
                    setIncomingMessage({
                      type: 'error',
                      reason: 'Missing Information!',
                    });
                  } else {
                    exportTemplateToHomeSubject
                      .pipe(first())
                      .subscribe((value) => {
                        setIncomingMessage(value);
                        setCurrentHash(value.hash);
                        setExisting({
                          templateName: templateTitle,
                          description,
                          tags,
                          hash: value.hash,
                        });
                      });
                    exportTradePlan({
                      data,
                      templateTitle,
                      tags,
                      description,
                    });
                    retrieveUserTemplates();
                  }
                }}
              >
                Export Template
              </TextButton>
            </div>
          ) : (
            <div className={style.hashAndUpdate}>
              <div>
                <div className={style.currentHash}>
                  <span className={style.templateCode}>{currentHash}</span>
                  {copied ? (
                    <span className={style.copied}>copied!</span>
                  ) : (
                    <IconButton
                      onClick={() => {
                        navigator.clipboard.writeText(currentHash);
                        setCopied(true);
                        const timer = setTimeout(() => {
                          setCopied(false);
                        }, 2000);
                        timeoutRef.current = timer;
                      }}
                    >
                      <FileCopyIcon />
                    </IconButton>
                  )}
                </div>
                <div className={style.copyToSocial}>
                  <TextButton
                    fontSize="small"
                    backgroundColor={colors.green}
                    width="48%"
                    clickHandler={copyToTwitter}
                  >
                    Share to Twitter
                  </TextButton>
                  <TextButton
                    fontSize="small"
                    backgroundColor={colors.green}
                    width="48%"
                    clickHandler={copyToClipboard}
                  >
                    Copy Share Message
                  </TextButton>
                </div>
              </div>
              <p
                className={
                  incomingMessage.type === 'error' ? style.error : style.success
                }
              >
                {incomingMessage.reason}
              </p>
              <TextButton
                fontSize="small"
                clickHandler={() => {
                  exportTemplateToHomeSubject
                    .pipe(first())
                    .subscribe((value) => {
                      setIncomingMessage(value);
                      setCurrentHash(value.hash);
                    });
                  exportTradePlan({
                    data,
                    templateTitle,
                    tags: existing.tags,
                    description: existing.description,
                  });
                  retrieveUserTemplates();
                }}
                color="white"
                backgroundColor="blue"
                width="100%"
              >
                Update Template
              </TextButton>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
});
ExportModal.propTypes = {
  data: PropTypes.object.isRequired,
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ExportModal;
